import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier, getBrands, getModels } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, MagnifyingGlass, Pencil, Trash, Factory, Car } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SuppliersPage = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    car_brands: [],
    car_models: [],
    notes: '',
    is_active: true
  });

  const loadData = useCallback(async () => {
    try {
      const [suppRes, brandRes, modelRes] = await Promise.all([
        getSuppliers({ search }),
        getBrands(),
        getModels({})
      ]);
      setSuppliers(suppRes.data);
      setBrands(brandRes.data);
      setModels(modelRes.data);
    } catch {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedSupplier) {
        await updateSupplier(selectedSupplier.id, formData);
        toast.success('Supplier updated');
      } else {
        await createSupplier(formData);
        toast.success('Supplier created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await deleteSupplier(supplier.id);
      toast.success('Supplier deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete supplier');
    }
  };

  const openEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name || '',
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      address: supplier.address || '',
      car_brands: supplier.car_brands || [],
      car_models: supplier.car_models || [],
      notes: supplier.notes || '',
      is_active: supplier.is_active ?? true
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedSupplier(null);
    setFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      car_brands: [],
      car_models: [],
      notes: '',
      is_active: true
    });
  };

  const toggleBrand = (brandId) => {
    const current = formData.car_brands;
    if (current.includes(brandId)) {
      setFormData({ ...formData, car_brands: current.filter(b => b !== brandId) });
    } else {
      setFormData({ ...formData, car_brands: [...current, brandId] });
    }
  };

  const toggleModel = (modelId) => {
    const current = formData.car_models;
    if (current.includes(modelId)) {
      setFormData({ ...formData, car_models: current.filter(m => m !== modelId) });
    } else {
      setFormData({ ...formData, car_models: [...current, modelId] });
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.name || brandId;
  };

  const getModelName = (modelId) => {
    const model = models.find(m => m.id === modelId);
    if (!model) return modelId;
    const brand = brands.find(b => b.id === model.brand_id);
    return `${brand?.name || ''} ${model.name}`.trim();
  };

  return (
    <div data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('suppliers.title')}</h1>
          <p className="text-muted-foreground text-sm">Manage your supplier network</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-supplier-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('suppliers.addSupplier')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-brutalist pl-10"
          data-testid="supplier-search"
        />
      </div>

      {/* Suppliers Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search ? 'No suppliers found matching your search' : 'No suppliers yet. Add your first supplier!'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier, idx) => (
            <div key={supplier.id} className={`card-brutalist animate-fade-in stagger-${(idx % 5) + 1}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                    <Factory weight="duotone" className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold">{supplier.name}</h3>
                    <span className={`text-xs px-2 py-0.5 ${supplier.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {supplier.is_active ? t('suppliers.active') : t('suppliers.inactive')}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(supplier)} data-testid={`edit-supplier-${supplier.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(supplier)} className="text-destructive" data-testid={`delete-supplier-${supplier.id}`}>
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {supplier.contact_email && (
                <p className="text-sm text-muted-foreground mb-1">{supplier.contact_email}</p>
              )}
              {supplier.contact_phone && (
                <p className="text-sm text-muted-foreground mb-3">{supplier.contact_phone}</p>
              )}
              
              {/* Brands Coverage */}
              {supplier.car_brands?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Brands:</p>
                  <div className="flex flex-wrap gap-1">
                    {supplier.car_brands.map((brandId) => (
                      <Badge key={brandId} variant="secondary" className="text-xs">
                        <Car className="w-3 h-3 mr-1" />
                        {getBrandName(brandId)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Models Coverage (specific models beyond brands) */}
              {supplier.car_models?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-2">Specific Models:</p>
                  <div className="flex flex-wrap gap-1">
                    {supplier.car_models.map((modelId) => (
                      <Badge key={modelId} variant="outline" className="text-xs">
                        {getModelName(modelId)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSupplier ? t('suppliers.editSupplier') : t('suppliers.addSupplier')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('suppliers.name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-brutalist"
                data-testid="supplier-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('suppliers.contactEmail')}</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="input-brutalist"
                  data-testid="supplier-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('suppliers.contactPhone')}</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="input-brutalist"
                  data-testid="supplier-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('suppliers.address')}</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-brutalist"
              />
            </div>
            
            {/* Brands & Models Coverage */}
            <div className="space-y-2">
              <Label>Vehicle Coverage</Label>
              <Tabs defaultValue="brands" className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="brands" className="flex-1">
                    Brands ({formData.car_brands.length})
                  </TabsTrigger>
                  <TabsTrigger value="models" className="flex-1">
                    Specific Models ({formData.car_models.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="brands" className="mt-2">
                  <div className="flex flex-wrap gap-2 p-3 border border-input bg-background max-h-40 overflow-y-auto">
                    {brands.length > 0 ? brands.map((brand) => (
                      <button
                        key={brand.id}
                        type="button"
                        onClick={() => toggleBrand(brand.id)}
                        className={`px-3 py-1 text-sm border transition-colors ${
                          formData.car_brands.includes(brand.id)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-secondary border-border hover:bg-secondary/80'
                        }`}
                      >
                        {brand.name}
                      </button>
                    )) : (
                      <p className="text-sm text-muted-foreground">No brands available. Add brands first.</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Select brands this supplier can provide</p>
                </TabsContent>
                <TabsContent value="models" className="mt-2">
                  <div className="flex flex-wrap gap-2 p-3 border border-input bg-background max-h-40 overflow-y-auto">
                    {models.length > 0 ? models.map((model) => {
                      const brand = brands.find(b => b.id === model.brand_id);
                      return (
                        <button
                          key={model.id}
                          type="button"
                          onClick={() => toggleModel(model.id)}
                          className={`px-3 py-1 text-sm border transition-colors ${
                            formData.car_models.includes(model.id)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-secondary border-border hover:bg-secondary/80'
                          }`}
                        >
                          {brand?.name} {model.name}
                        </button>
                      );
                    }) : (
                      <p className="text-sm text-muted-foreground">No models available. Add models first.</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Select specific models (for suppliers that don't cover full brands)</p>
                </TabsContent>
              </Tabs>
            </div>
            
            <div className="space-y-2">
              <Label>{t('suppliers.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="input-brutalist"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('suppliers.active')}</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="supplier-submit-btn">
                {t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
